import { measure } from '@/components/Analytics/Analytics';
import AudioPlayer from '@/components/AudioPlayer/AudioPlayer';
import Button from '@/components/Button/Button';
import ExpandableImage from '@/components/ExpandableImage/ExpandableImage';
import AddIcon from '@/components/Icon/AddIcon';
import CameraIcon from '@/components/Icon/CameraIcon';
import RecorderIcon from '@/components/Icon/RecorderIcon';
import TopOverlayPattern from '@/components/TopOverlayPattern/TopOverlayPattern';
import useCards from '@/hooks/cards/useCards';
import useSpecies from '@/hooks/species/useSpecies';
import useHaptics from '@/hooks/useHaptics';
import { getMimeTypeFromDataUrl } from '@/lib/file';
import { motion } from 'framer-motion';
import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const OepsRoute = () => {
	const { state } = useLocation();
	const navigate = useNavigate();
	const { findSpeciesById, getMainImageById, getSpeciesRouteById } =
		useSpecies();
	const { storeMedia } = useCards();
	const { applyHapticNotification } = useHaptics();

	// Remember the state from location on every render
	const image = useRef<string | undefined>(state?.image);
	const audio = useRef<string | undefined>(state?.audio);
	const targetSpeciesId = useRef(state?.targetSpeciesId);
	const resultIdentifiedAsId = useRef<number | null | undefined>(
		state?.resultIdentifiedAsId,
	);
	const resultIdentifiedAsDisplayName = useRef(
		state?.resultIdentifiedAsDisplayName,
	);

	const isConnectionError =
		!resultIdentifiedAsId.current || !resultIdentifiedAsDisplayName.current;

	// Find the species
	const species = useMemo(
		() => findSpeciesById(targetSpeciesId.current),
		[findSpeciesById],
	);

	// Get the species main image
	const mainImage = useMemo(
		() => getMainImageById(targetSpeciesId.current),
		[getMainImageById],
	);

	// Find the identified as species, if any
	const identifiedAsOtherSpecies = useMemo(
		() =>
			resultIdentifiedAsId.current &&
			resultIdentifiedAsId.current !== targetSpeciesId.current
				? findSpeciesById(resultIdentifiedAsId.current)
				: null,
		[findSpeciesById],
	);

	// Get the main image of the identified as species
	const mainImageIdentifiedAs = useMemo(
		() =>
			identifiedAsOtherSpecies
				? getMainImageById(identifiedAsOtherSpecies.id)
				: null,
		[getMainImageById, identifiedAsOtherSpecies],
	);

	// Retry (go back to camera)
	const retry = useCallback(() => {
		measure(
			`camera-error-retry-${targetSpeciesId.current}-${species?.attributes.slug}`,
		);
		navigate(-1);
	}, [navigate, species?.attributes.slug]);

	// Store card anyways if user wishes to do so
	const storeAnyways = useCallback(
		(
			storeAsId: number,
			storeAsSlug: string,
			isManuallyIdentified: boolean,
		) => {
			measure(
				`camera-error-identification-manual-${storeAsId}-${storeAsSlug}`,
			);
			storeMedia(
				storeAsId,
				image.current ? 'photo' : 'audio',
				(image.current ?? audio.current) as string,
				resultIdentifiedAsDisplayName.current,
				isManuallyIdentified ? 'manually' : 'automatically',
			);
			navigate(-1);
			setTimeout(() => {
				navigate(getSpeciesRouteById(storeAsId), {
					replace: true,
				});
			}, 500);
		},
		[storeMedia, navigate, getSpeciesRouteById],
	);

	// If the state is not set or species is not found, return null
	if (
		[
			mainImage,
			species,
			isConnectionError ?? image.current ?? audio.current,
			targetSpeciesId.current,
		].some((value) => value === undefined)
	)
		return null;

	return (
		<div className="absolute inset-0 size-full overflow-hidden bg-primary">
			<audio
				src="/assets/sounds/negative.mp3"
				autoPlay
				aria-hidden
				className="hidden"
				onPlay={() => applyHapticNotification('error')}
			/>
			<TopOverlayPattern className="absolute inset-0 h-64 w-full -rotate-3 scale-[6] text-secondary opacity-30" />
			<main className="absolute inset-0 grid size-full min-h-full items-center justify-center overflow-y-auto bg-black/40 p-8 text-gray-50">
				<motion.h1
					className="-rotate-2 text-center font-brand text-7xl"
					initial={{
						scale: 0.5,
					}}
					animate={{
						scale: [0.5, 1.1, 1],
						rotate: [0, -2, 2, -2],
						x: ['2rem', '-2rem', '1rem', '-1rem', '1rem', '0rem'],
						transition: {
							delay: 0.2,
							duration: 1,
						},
					}}>
					Oeps!
				</motion.h1>
				<p className="text-balance pb-4 text-center text-xl">
					{!!image.current &&
						!isConnectionError &&
						`Deze foto lijkt meer op een ${resultIdentifiedAsDisplayName.current}`}
					{!!audio.current &&
						!isConnectionError &&
						`Dit geluid lijkt meer op een ${resultIdentifiedAsDisplayName.current}`}
					{isConnectionError &&
						'Er is iets misgegaan. Je kan het opnieuw proberen of zelf opslaan.'}
				</p>
				<div className="mx-auto flex flex-col items-start gap-6 xxs:flex-row sm:gap-8">
					{!!image.current && (
						<ExpandableImage
							src={image.current}
							thumbnailSrc={image.current}
							alt="De foto die je net hebt gemaakt"
							className="-mt-2 h-56 w-36 shrink-0 sm:w-44"
							caption={resultIdentifiedAsDisplayName.current}
						/>
					)}
					{!!audio.current && (
						<AudioPlayer
							src={audio.current}
							mimeType={getMimeTypeFromDataUrl(audio.current)}
						/>
					)}
					{!!mainImage?.url && (
						<ExpandableImage
							src={mainImage.url}
							thumbnailSrc={mainImage.thumbnailUrl}
							alt={mainImage.alt}
							caption={species?.attributes.titleShort}
							className="mt-2 size-36 shrink-0 sm:size-44"
						/>
					)}
				</div>
				<div className="flex flex-col items-center gap-6 py-6">
					<Button
						onClick={retry}
						label="Nog een keer proberen"
						backgroundClassName="text-accent"
						textClassName="text-accentCompat text-3xl ps-4 py-2 gap-0"
						icon={
							image.current ? (
								<CameraIcon className="h-16 shrink-0 grow-0 lg:h-16" />
							) : (
								<RecorderIcon className="h-16 shrink-0 grow-0 lg:h-16" />
							)
						}
					/>
					{!!species?.attributes.slug && (
						<Button
							onClick={() => {
								storeAnyways(
									targetSpeciesId.current,
									species?.attributes.slug,
									true,
								);
							}}
							label={`Opslaan als ${species?.attributes.titleShort}`}
							backgroundClassName="text-primary"
							textClassName="ps-4 text-2xl py-1"
							image={
								mainImage
									? {
											src: mainImage.thumbnailUrl,
											alt: mainImage.alt,
										}
									: undefined
							}
							icon={
								<AddIcon className="h-12 w-fit shrink-0 grow-0 lg:h-12" />
							}
						/>
					)}
					{identifiedAsOtherSpecies && mainImageIdentifiedAs && (
						<Button
							onClick={() => {
								storeAnyways(
									identifiedAsOtherSpecies.id,
									identifiedAsOtherSpecies.attributes.slug,
									false,
								);
							}}
							label={`Opslaan bij ${identifiedAsOtherSpecies.attributes.titleShort}`}
							backgroundClassName="text-secondary"
							textClassName="ps-4 text-2xl py-1"
							image={{
								src: mainImageIdentifiedAs?.thumbnailUrl,
								alt: mainImageIdentifiedAs?.alt,
							}}
							icon={
								<AddIcon className="h-12 w-fit shrink-0 lg:h-12" />
							}
						/>
					)}
				</div>
			</main>
		</div>
	);
};

export default OepsRoute;
